import { useState } from "react";
import { useLocation } from "wouter";
import { trackEvent } from "@/App";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Zap, ListChecks, PenLine, ClipboardList, ArrowRight } from "lucide-react";

const OPTIONS = [
  {
    id: "auto",
    icon: Zap,
    label: "Auto Quote",
    description: "Upload drawings or photos and our quoting engine builds a fully priced quote in minutes.",
    href: "/quotes/new",
    testId: "button-modal-auto-quote",
  },
  {
    id: "template",
    icon: ListChecks,
    label: "Template Quote",
    description: "Pick a project type (extension, loft, bathroom) and get a pre-loaded scope instantly.",
    href: "/quotes/template",
    testId: "button-modal-template-quote",
  },
  {
    id: "manual",
    icon: PenLine,
    label: "Manual Quote",
    description: "Search 462 rated tasks and build a quote line by line from your Cost Library.",
    href: "/quotes/manual",
    testId: "button-modal-manual-quote",
  },
  {
    id: "survey",
    icon: ClipboardList,
    label: "Site Survey",
    description: "Walk a property capturing voice notes, photos and work items per room. Produces a Site Visit Report you can send straight to Auto Quote.",
    href: "/quotes/survey",
    testId: "button-modal-site-survey",
  },
];

interface NewQuoteModalProps {
  open: boolean;
  onClose: () => void;
}

export function NewQuoteModal({ open, onClose }: NewQuoteModalProps) {
  const [, navigate] = useLocation();

  const handleSelect = (href: string, id: string) => {
    trackEvent("quote_started", href, { type: id });
    onClose();
    navigate(href);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="modal-new-quote">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Create a New Quote</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1">Choose how you want to build this quote.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-2">
          {OPTIONS.map(({ id, icon: Icon, label, description, href, testId }) => (
            <button
              key={id}
              data-testid={testId}
              onClick={() => handleSelect(href, id)}
              className="flex items-start gap-4 w-full text-left rounded-lg border border-border p-4 transition-colors hover:border-primary/50 hover:bg-primary/5 group"
            >
              <div className="mt-0.5 flex-shrink-0 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-primary">{label}</div>
                <div className="text-sm text-muted-foreground leading-snug mt-0.5">{description}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useNewQuoteModal() {
  const [open, setOpen] = useState(false);
  return {
    open,
    openModal: () => setOpen(true),
    closeModal: () => setOpen(false),
  };
}
